/* "Grace Community Church has a question for you", at the top of Home.
 *
 * A church that adds a question after people joined would otherwise never
 * hear from them: nobody revisits a form they filled in once. The server
 * decides what is pending — a required question still unanswered, or any
 * question added since they last looked — and "Not now" quiets the optional
 * ones. A required one comes back until it is answered.
 */
import { useNavigate } from 'react-router-dom'
import { MessageCircleQuestion } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { CustomField } from '@/types/api'

export interface MyChurchAnswers {
  church_id: string
  church_name: string
  fields: CustomField[]
  answers: Record<string, string>
  pending: string[]
}

export const MY_CHURCH_ANSWERS_KEY = ['me', 'church-answers'] as const

export function useMyChurchAnswers(enabled: boolean) {
  return useQuery({
    queryKey: MY_CHURCH_ANSWERS_KEY,
    queryFn: () => api.get<MyChurchAnswers>('/me/church-answers'),
    enabled,
    retry: false,
  })
}

export function QuestionsNudge({ inChurch }: { inChurch: boolean }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const mine = useMyChurchAnswers(inChurch)
  const later = useMutation({
    mutationFn: () => api.post<MyChurchAnswers>('/me/church-answers/seen'),
    onSuccess: (next) => qc.setQueryData(MY_CHURCH_ANSWERS_KEY, next),
  })

  const d = mine.data
  if (!inChurch || !d || !d.pending.length) return null

  const n = d.pending.length
  const required = d.fields.some(
    (f) => f.is_required && d.pending.includes(f.id),
  )

  return (
    <div className="cn-card flex flex-wrap items-center gap-s3 px-s4 py-s3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
        <MessageCircleQuestion className="h-5 w-5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base font-bold">
          {d.church_name} has {n === 1 ? 'a question' : `${n} questions`} for you
        </span>
        <span className="block text-sm text-ink-2">
          It helps your church know you and keep in touch.
        </span>
      </span>
      <span className="flex shrink-0 gap-s2">
        {required ? null : (
          <button
            type="button"
            disabled={later.isPending}
            onClick={() => later.mutate()}
            className="rounded-pill px-s3 py-1.5 text-sm font-semibold text-ink-2 hover:bg-hover"
          >
            Not now
          </button>
        )}
        <button
          type="button"
          onClick={() => navigate('/settings/church')}
          className="rounded-pill bg-brand px-s3 py-1.5 text-sm font-bold text-ink-on-brand hover:bg-brand-hover"
        >
          Answer
        </button>
      </span>
    </div>
  )
}
